import React, { useEffect, useMemo, useRef } from "react";
import cx from 'classnames';
import { CollectionAsset, CollectionEditState } from "../../../../../types/collections";

import Thumbnail from "../../../../../components/thumbnail";
import LinesEllipsis from "react-lines-ellipsis";
import Reveal from "../../../../../components/animation";
import { FileService } from "../../../../../utils/file.service";

import styles from './card.module.scss';
import { BentoContext, BentoContextType } from "../layout";
import _ from "lodash";
import Tooltip from "src/components/tooltip";
import { NavigateEntity } from "src/components/NavigateEntity";


const DIMENSIONS = [{
  id: 'one',
  width: 1,
  height: 1
}, {
  id: 'two',
  width: 2,
  height: 1
}, {
  id: 'three',
  width: 1,
  height: 2
}, {
  id: 'four',
  width: 2,
  height: 2
}]

interface AssetCardProps {
  asset: CollectionAsset;
  editable: boolean;
  collection : CollectionEditState;
  sectionIndex? : number;

  // eslint-disable-next-line no-unused-vars
  onClick: (asset: CollectionAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: CollectionAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onResize?: (asset: CollectionAsset, width: number, height: number) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, editable, onRemove, onResize, onClick, collection, sectionIndex }) => {
  const initialised = useRef(false);
  const assetItem = asset.asset;

  const { gridStack } = React.useContext<BentoContextType>(BentoContext);

  const assetRef = useRef(null);
  const x = asset.metadata?.xaxis || 0;
  const y = asset.metadata?.yaxis || 0;
  const width = asset.metadata?.width || 1;
  const height = asset.metadata?.height || 1;

  const fileType = useMemo(() => {
    return FileService.getFileType(assetItem.metadata);
  }, [assetItem]);

  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if(!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  const internal = !_.get(assetItem, 'settings.share', false);

  useEffect(() => {
    if(gridStack?.current && assetRef.current && asset.metadata){
      const { xaxis, yaxis, width, height } = asset.metadata;

      if (!initialised.current){
        gridStack.current.makeWidget(assetRef.current);
        initialised.current = true;
      }else {
        gridStack.current.update(assetRef.current, { x: xaxis, y: yaxis, w: width, h: height });
      }
    }
  }, [assetRef.current, asset.metadata]);

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  const handleResize = (width: number, height: number) => {
    if (onResize) {
      onResize(asset, width, height);
    }
  }

  const handleClick = (e) => {
    e.preventDefault();
    if(!editable && onClick){
      onClick(asset);
    }
  }

  const getAssetUrl = (asset: CollectionAsset, section: number) => {
    const sectionIndex = section ? section : 0;
    let source = `collections/${collection.id}`;
    if (sectionIndex >= 0) {
      source = `${source}/sections/${sectionIndex}`;
    }
    return `/assets/${asset.asset.id}?source=${source}`;
  };

  const assetUrl = getAssetUrl(asset, sectionIndex ?? 0);

  return (
    <div className={cx(styles.card, 'grid-stack-item', {
      [styles.editable]: editable,
    })}
      ref={assetRef}
      // eslint-disable-next-line react/no-unknown-property
      gs-id={asset.id}
      // eslint-disable-next-line react/no-unknown-property
      gs-w={width}
      // eslint-disable-next-line react/no-unknown-property
      gs-h={height}
      // eslint-disable-next-line react/no-unknown-property
      gs-x={x}
      // eslint-disable-next-line react/no-unknown-property
      gs-y={y}
      // eslint-disable-next-line react/no-unknown-property
      gs-no-resize="true"
    >
      <div className={cx("grid-stack-item-content", styles.inner)}>
      <NavigateEntity 
        path={assetUrl}
        onClick={handleClick}
        disabledClick={editable}
      >
        <div className={styles.icon}>
          <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
        </div>

        <div className={styles.overlay}></div>

        <Reveal show={editable}>
          <button className={styles.remove} onClick={handleRemove}>
            <i className="paperflite-close"></i>
          </button>
        </Reveal>

        {internal && 
          <div className={styles.indicators}>
          <Tooltip content="Internal use only" placement="auto">
            <i className="paperflite-lock" />
          </Tooltip>
          </div>
        }

        <div className={styles.fileType}>{fileType}</div>

        <div className={styles.controls}>
          <div className={styles.cardContent}>
            <div className={styles.name}>
              {assetItem.name || 'Untitled'}
            </div>
            <div className={styles.meta}>{fileType} {fileSize && `| ${fileSize}`}</div>
          </div>

          <ResizeControl width={width} height={height} onResize={handleResize} />
        </div>
      </NavigateEntity>
      </div>
    </div>
  );
}

interface ResizeControlProps {
  width: number;
  height: number;
  // eslint-disable-next-line no-unused-vars
  onResize: (width: number, height: number) => void;
}

const ResizeControl: React.FC<ResizeControlProps> = ({ width, height, onResize }) => {
  return (
    <div className={styles.resizeControl}>
      {DIMENSIONS.map((dimension) => {
        const selected = dimension.width === width && dimension.height === height;
        return (
          <div key={dimension.id} className={cx(styles.resize, styles[dimension.id], {[styles.selected]: selected})} 
            onClick={() => onResize(dimension.width, dimension.height)}></div>
        );
      })}
    </div>
  );
}


const PlaceholderAssetCard: React.FC<{height: string}> = ({height}) => {
  return (
    <div className={cx(styles.card, styles.placeholder)} style={{height: height}}>
      <div className={cx(styles.icon, styles.block)}></div>
    </div>
  );
}

const DraggingAssetCard: React.FC<{ asset: CollectionAsset, overSection: boolean }> = ({ asset, overSection }) => {
  const assetItem = asset.asset;

  const fileType = useMemo(() => {
    return FileService.getFileType(assetItem.metadata);
  }, [assetItem]);

  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if(!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  return (
    <div className={cx(styles.card, { [styles.overSection]: overSection })}>
      <div className={styles.icon}>
        <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          {assetItem.name || 'Untitled'}
        </div>
        <div className={styles.meta}>{fileType} {fileSize && `| ${fileSize}`}</div>
      </div>
      <div className={styles.message}>Add to section</div>
    </div>
  );
}

export { AssetCard, type AssetCardProps, PlaceholderAssetCard, DraggingAssetCard };